/**
 * Two views (SPEC decision 22, plan P23): platform operators (Malible staff) switch between **Hangar administration**
 * (clients, providers, the client preview) and the **workspace** of their own org; everyone else only has the
 * workspace. Tutorials appear in both (the API filters the lessons by audience).
 *
 * Sidebar navigation (design/DESIGN.md → Layout): only the screens of the MVP (docs/mvp-plan.md §1.1). Screens of later
 * phases (queue, releases, OTA updates, approvals, audit log, docs/SDK) are not listed at all until they exist: a
 * disabled "soon" entry only adds noise (José, 2026-10-28). Settings opens the current project's settings (design 28).
 * The entries are grouped by the work (SPEC decision 30): Overview, Getting started and Tutorials on top; Set up;
 * Build; Organization.
 */
export interface NavItem {
  slug: string;
  label: string;
  /** Short label for the phone tab bar. */
  short?: string;
  icon: string;
  live: true;
  /** Route builder for live items; `slug` is the current project's slug (undefined when there is none yet). */
  href: (projectSlug: string | undefined) => string;
  /** Shown to platform operators (Malible staff) only. */
  operatorOnly?: boolean;
  /** The views that list it; default: the workspace only. */
  views?: readonly ShellView[];
  /** The workspace group it belongs to (SPEC decision 30). */
  group: NavGroupId;
}

export type ShellView = 'admin' | 'workspace';

/**
 * Workspace groups (SPEC decision 30, plan P32): the sidebar follows the work. `start` has no label (Overview, Getting
 * started, Tutorials on top); `admin` holds Hangar administration, which keeps its own view and order.
 */
export type NavGroupId = 'start' | 'setup' | 'build' | 'organization' | 'admin';

export const NAV_GROUP_LABEL: Record<NavGroupId, string | null> = { start: null, setup: 'Set up', build: 'Build', organization: 'Organization', admin: 'Hangar administration' };

const WORKSPACE_GROUPS: readonly NavGroupId[] = ['start', 'setup', 'build', 'organization'];

export interface NavGroup {
  id: NavGroupId;
  /** The quiet kicker above the entries; null for the top group (and for the one group of administration). */
  label: string | null;
  items: NavItem[];
}

const projectPath = (segment: string) => (slug: string | undefined) => (slug ? `/projects/${slug}${segment}` : '/projects');

/** In workspace order within each group; administration lists its own entries in this order too (Clients, Expo & Bitrise, Architecture, Tutorials). */
export const NAV: NavItem[] = [
  { slug: 'overview', label: 'Overview', icon: 'dashboard', live: true, href: () => '/', group: 'start' },
  { slug: 'getting-started', label: 'Getting started', short: 'Start', icon: 'checklist', live: true, href: () => '/getting-started', group: 'start' },
  { slug: 'integrations', label: 'Integrations', icon: 'hub', live: true, href: () => '/integrations', group: 'setup' },
  { slug: 'apps', label: 'Apps & profiles', short: 'Apps', icon: 'apps', live: true, href: (slug) => (slug ? `/projects/${slug}` : '/projects'), group: 'setup' },
  { slug: 'environment-variables', label: 'Environment variables', short: 'Env vars', icon: 'tune', live: true, href: projectPath('/env'), group: 'setup' },
  { slug: 'credentials', label: 'Credentials', icon: 'key', live: true, href: projectPath('/credentials'), group: 'setup' },
  { slug: 'devices', label: 'Devices', icon: 'phone_iphone', live: true, href: () => '/devices', group: 'setup' },
  { slug: 'builds', label: 'Builds', icon: 'construction', live: true, href: projectPath('/builds'), group: 'build' },
  { slug: 'access', label: 'Access', icon: 'shield', live: true, href: () => '/access', group: 'organization' },
  { slug: 'usage-limits', label: 'Usage', icon: 'monitoring', live: true, href: () => '/usage', group: 'organization' },
  { slug: 'settings', label: 'Settings', icon: 'settings', live: true, href: projectPath('/settings'), group: 'organization' },
  { slug: 'clients', label: 'Clients', icon: 'domain', live: true, href: () => '/clients', operatorOnly: true, views: ['admin'], group: 'admin' },
  { slug: 'platform-providers', label: 'Expo & Bitrise', short: 'Providers', icon: 'vpn_key', live: true, href: () => '/platform-providers', operatorOnly: true, views: ['admin'], group: 'admin' },
  { slug: 'architecture', label: 'Architecture', icon: 'account_tree', live: true, href: () => '/architecture', operatorOnly: true, views: ['admin'], group: 'admin' },
  { slug: 'tutorials', label: 'Tutorials', short: 'Learn', icon: 'school', live: true, href: () => '/tutorials', views: ['admin', 'workspace'], group: 'start' },
];

/** Whether `item` is offered to this viewer: operator-only items need `me.user.platformRole` (unknown viewer: hidden). */
export const isNavVisible = (item: NavItem, me: { user?: { platformRole?: string | null } | null } | undefined): boolean =>
  !item.operatorOnly || me?.user?.platformRole === 'operator';

/**
 * The view a page belongs to: Clients, Expo & Bitrise and Architecture are administration, Tutorials keeps the view the operator came from, everything
 * else is the workspace. Anyone who is not an operator (a client previewed included) only has the workspace.
 */
export function viewOf(section: Section, operator: boolean, previous: ShellView): ShellView {
  if (!operator) return 'workspace';
  if (section === 'clients' || section === 'platform-providers' || section === 'architecture') return 'admin';
  return section === 'tutorials' ? previous : 'workspace';
}

/** The entries of `view` this viewer may open, in sidebar order. */
export const navFor = (view: ShellView, me: Parameters<typeof isNavVisible>[1]): NavItem[] =>
  NAV.filter((item) => isNavVisible(item, me) && (item.views ?? ['workspace']).includes(view));

/**
 * The sidebar's groups for `view` (desktop sidebar and tablet rail): the workspace in its four groups, empty groups
 * dropped; Hangar administration as one unlabeled group, as it always was.
 */
export function navGroupsFor(view: ShellView, me: Parameters<typeof isNavVisible>[1]): NavGroup[] {
  const items = navFor(view, me);
  if (view === 'admin') return items.length ? [{ id: 'admin', label: null, items }] : [];
  return groupsOf(items, WORKSPACE_GROUPS);
}

/** `items` split by group in `order`, each group keeping its items' order; a group with no item is left out. */
export function groupsOf(items: readonly NavItem[], order: readonly NavGroupId[]): NavGroup[] {
  return order.map((id) => ({ id, label: NAV_GROUP_LABEL[id], items: items.filter((i) => i.group === id) })).filter((g) => g.items.length > 0);
}

/**
 * The phone's More sheet: every entry that is not a tab, in the workspace groups, then Hangar administration (operators
 * only; the phone has no view switch, so administration is a labeled group of its own).
 */
export function moreGroups(me: Parameters<typeof isNavVisible>[1], tabSlugs: readonly string[]): NavGroup[] {
  const items = NAV.filter((i) => isNavVisible(i, me) && !tabSlugs.includes(i.slug));
  return groupsOf(items, [...WORKSPACE_GROUPS, 'admin']);
}

export type Section = 'overview' | 'getting-started' | 'builds' | 'env' | 'apps' | 'integrations' | 'access' | 'usage' | 'credentials' | 'devices' | 'settings' | 'clients' | 'platform-providers' | 'tutorials' | 'architecture';

/** Which nav item a pathname belongs to (drives `aria-current` and the breadcrumb). */
export function sectionOf(pathname: string): Section {
  if (pathname.startsWith('/integrations')) return 'integrations';
  if (pathname.startsWith('/access')) return 'access';
  if (pathname.startsWith('/usage')) return 'usage';
  if (pathname.startsWith('/clients')) return 'clients';
  if (pathname.startsWith('/tutorials')) return 'tutorials';
  if (pathname.startsWith('/architecture')) return 'architecture';
  if (pathname.startsWith('/platform-providers')) return 'platform-providers';
  if (pathname.startsWith('/getting-started')) return 'getting-started';
  if (pathname.startsWith('/devices')) return 'devices';
  if (/^\/projects\/[^/]+\/builds/.test(pathname)) return 'builds';
  if (/^\/projects\/[^/]+\/env/.test(pathname)) return 'env';
  if (/^\/projects\/[^/]+\/settings/.test(pathname)) return 'settings';
  if (/^\/projects\/[^/]+\/credentials/.test(pathname)) return 'credentials';
  if (pathname.startsWith('/projects')) return 'apps';
  return 'overview';
}

export const SECTION_LABEL: Record<Section, string> = { overview: 'Overview', 'getting-started': 'Getting started', builds: 'Builds', env: 'Environment variables', apps: 'Apps & profiles', integrations: 'Integrations', access: 'Access', usage: 'Usage', credentials: 'Credentials', devices: 'Devices', settings: 'Project settings', clients: 'Clients', 'platform-providers': 'Expo & Bitrise', tutorials: 'Tutorials', architecture: 'Architecture' };
export const SECTION_SLUG: Record<Section, string> = { overview: 'overview', 'getting-started': 'getting-started', builds: 'builds', env: 'environment-variables', apps: 'apps', integrations: 'integrations', access: 'access', usage: 'usage-limits', credentials: 'credentials', devices: 'devices', settings: 'settings', clients: 'clients', 'platform-providers': 'platform-providers', tutorials: 'tutorials', architecture: 'architecture' };
